import { useCallback, useEffect, useRef, useState } from "react"
import Image from "next/image"

import { Badge, Button } from "@coval/ui"
import { Input } from "@heroui/react"
import { ImagePlus, Replace, Trash2 } from "lucide-react"

import {
  ALLOWED_MEDIA_MIME_TYPES,
  MAX_MEDIA_FILE_SIZE_BYTES,
  type MediaAttachment,
} from "@/types/TestCaseInput"

/** Local state for a media attachment before S3 upload. */
export interface LocalMediaAttachment {
  file: File
  name: string
  description: string
  previewUrl: string
}

interface MediaAttachmentSectionProps {
  localAttachment: LocalMediaAttachment | null
  persistedAttachment: MediaAttachment | null
  persistedPreviewUrl: string | null
  canUpload: boolean
  onAttachmentChange: (attachment: LocalMediaAttachment | null) => void
  onPersistedAttachmentChange: (
    updates: { name: string; description?: string } | null,
  ) => void
}

const ACCEPT = ALLOWED_MEDIA_MIME_TYPES.join(",")
const MAX_SIZE_MB = MAX_MEDIA_FILE_SIZE_BYTES / (1024 * 1024)

export default function MediaAttachmentSection({
  localAttachment,
  persistedAttachment,
  persistedPreviewUrl,
  canUpload,
  onAttachmentChange,
  onPersistedAttachmentChange,
}: MediaAttachmentSectionProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const fileDialogCellRef = useRef<HTMLTableCellElement | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)

  const handleWindowFocusAfterDialog = useCallback(() => {
    const cell = fileDialogCellRef.current
    if (cell) {
      cell.removeAttribute("data-file-dialog-open")
      cell.focus()
    }
    fileDialogCellRef.current = null
    window.removeEventListener("focus", handleWindowFocusAfterDialog)
  }, [])

  const hasLocalAttachment = localAttachment !== null
  const hasPersistedAttachment = persistedAttachment !== null
  const hasAttachment = hasLocalAttachment || hasPersistedAttachment

  const displayName = hasLocalAttachment
    ? localAttachment.name
    : (persistedAttachment?.name ?? "")
  const displayDescription = hasLocalAttachment
    ? localAttachment.description
    : (persistedAttachment?.description ?? "")
  const displayFilename = hasLocalAttachment
    ? localAttachment.file.name
    : (persistedAttachment?.name ?? "Attached image")
  const previewUrl = hasLocalAttachment
    ? localAttachment.previewUrl
    : persistedPreviewUrl

  // Revoke object URLs when local preview changes / unmounts.
  useEffect(() => {
    const previewUrl = localAttachment?.previewUrl
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [localAttachment?.previewUrl])

  // Clean up file-dialog focus state on unmount.
  useEffect(() => {
    return () => {
      const cell = fileDialogCellRef.current
      if (cell) {
        cell.removeAttribute("data-file-dialog-open")
      }
      window.removeEventListener("focus", handleWindowFocusAfterDialog)
    }
  }, [handleWindowFocusAfterDialog])

  const handleFileSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) {
        return
      }

      // Reset the input so the same file can be re-selected after removal
      e.target.value = ""

      if (
        !ALLOWED_MEDIA_MIME_TYPES.includes(
          file.type as (typeof ALLOWED_MEDIA_MIME_TYPES)[number],
        )
      ) {
        setFileError("Only PNG and JPEG images are allowed.")
        return
      }

      if (file.size > MAX_MEDIA_FILE_SIZE_BYTES) {
        setFileError(`File exceeds the ${MAX_SIZE_MB} MB limit.`)
        return
      }

      setFileError(null)

      // Revoke previous preview URL to avoid memory leaks
      if (localAttachment?.previewUrl) {
        URL.revokeObjectURL(localAttachment.previewUrl)
      }

      const previewUrl = URL.createObjectURL(file)
      const defaultName = file.name.replace(/\.[^.]+$/, "").replace(/\s+/g, "_")

      onAttachmentChange({
        file,
        name: localAttachment?.name || persistedAttachment?.name || defaultName,
        description:
          localAttachment?.description ||
          persistedAttachment?.description ||
          "",
        previewUrl,
      })
    },
    [localAttachment, persistedAttachment, onAttachmentChange],
  )

  const handleRemove = useCallback(() => {
    if (localAttachment?.previewUrl) {
      URL.revokeObjectURL(localAttachment.previewUrl)
    }
    onAttachmentChange(null)
    if (persistedAttachment) {
      onPersistedAttachmentChange(null)
    }
    setFileError(null)
  }, [
    localAttachment,
    persistedAttachment,
    onAttachmentChange,
    onPersistedAttachmentChange,
  ])

  const handleNameChange = useCallback(
    (value: string) => {
      if (hasLocalAttachment) {
        onAttachmentChange({ ...localAttachment, name: value })
        return
      }
      if (!hasPersistedAttachment) {
        return
      }
      onPersistedAttachmentChange({
        name: value,
        description: persistedAttachment.description,
      })
    },
    [
      hasLocalAttachment,
      localAttachment,
      hasPersistedAttachment,
      persistedAttachment,
      onAttachmentChange,
      onPersistedAttachmentChange,
    ],
  )

  const handleDescriptionChange = useCallback(
    (value: string) => {
      if (hasLocalAttachment) {
        onAttachmentChange({ ...localAttachment, description: value })
        return
      }
      if (!hasPersistedAttachment) {
        return
      }
      onPersistedAttachmentChange({
        name: persistedAttachment.name,
        description: value || undefined,
      })
    },
    [
      hasLocalAttachment,
      localAttachment,
      hasPersistedAttachment,
      persistedAttachment,
      onAttachmentChange,
      onPersistedAttachmentChange,
    ],
  )

  const openFilePicker = useCallback(() => {
    // Mark the parent cell to suppress blur-triggered collapse while
    // the native file dialog is open. The cell's onBlur checks this attribute.
    const cell = fileInputRef.current?.closest(
      "td",
    ) as HTMLTableCellElement | null
    if (cell) {
      window.removeEventListener("focus", handleWindowFocusAfterDialog)
      fileDialogCellRef.current = cell
      cell.setAttribute("data-file-dialog-open", "true")
      window.addEventListener("focus", handleWindowFocusAfterDialog)
    }
    fileInputRef.current?.click()
  }, [handleWindowFocusAfterDialog])

  // Prevent button mousedown from moving focus away from the parent cell wrapper,
  // which would collapse the editing state in the inline table editor.
  const preventBlur = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
  }, [])

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-coval-text">Media Attachment</p>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        onChange={handleFileSelected}
        className="hidden"
        aria-label="Select media file"
      />

      {!hasAttachment && canUpload && (
        <Button
          variant="secondary"
          onClick={openFilePicker}
          onMouseDown={preventBlur}
          aria-label="Add media attachment"
        >
          <ImagePlus className="w-4 h-4" />
          Add Media
        </Button>
      )}

      {fileError && (
        <p className="text-sm text-coval-danger-text" role="alert">
          {fileError}
        </p>
      )}

      {hasAttachment && (
        <div className="space-y-3 rounded-lg border border-coval-border bg-surface-0 p-3">
          <div className="flex items-start gap-3">
            <Image
              src={
                previewUrl ||
                "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw=="
              }
              alt={displayName || "Attached image"}
              width={80}
              height={80}
              className="h-20 w-20 rounded-md object-cover border border-coval-border"
              unoptimized
            />
            <div className="flex-1 space-y-2">
              <Badge size="status" variant="success">
                {displayFilename}
              </Badge>
              <Input
                size="sm"
                label="Name"
                placeholder="e.g. broken_screen_photo"
                value={displayName}
                onValueChange={handleNameChange}
                isDisabled={!canUpload}
                isRequired
                aria-label="Attachment name"
              />
              <Input
                size="sm"
                label="Description (optional)"
                placeholder="Describe the image for the persona"
                value={displayDescription}
                onValueChange={handleDescriptionChange}
                isDisabled={!canUpload}
                aria-label="Attachment description"
              />
            </div>
          </div>
          <div className="flex gap-2">
            {canUpload && (
              <Button
                variant="secondary"
                onClick={openFilePicker}
                onMouseDown={preventBlur}
                aria-label="Replace media attachment"
              >
                <Replace className="w-4 h-4" />
                Replace
              </Button>
            )}
            <Button
              variant="danger"
              onClick={handleRemove}
              onMouseDown={preventBlur}
              aria-label="Remove media attachment"
            >
              <Trash2 className="w-4 h-4" />
              Remove
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
